import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useMemo, useCallback, } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fab, } from '@mui/material';
import { VoiceChat as VirtualCoachIcon } from '@mui/icons-material';
import { getSignedAssetUrl, useStore } from '@mcn-platform/models';
import { ConversationDialog, conversationDialogDefaults, conversationDialogMeta, ConversationStoreProvider, SelectionWizardProvider, Tokens, } from '@mcn-platform/converse-lib';
import ConversationStoreAdapter from './ConversationStoreAdapter';
const CoachFloatingWidget = ({ title = 'Virtual Coach', showItemReferences = false, iconAssetId, backgroundColor, padding, courseCardShowImage, courseCardShowTitle, courseCardShowInstructor, courseCardShowDescLines, courseCardShowPrice, clientAIAnimatedProcessingIcon, openWebPagesInNewTab = false, hideRightRailOnMobile = false, enableMultiRefinements, multiRefinementSubmitButtonText, maxItemsInRightRail, questionStyle, responseStyle, inputStyle, startingMessageFontStyle, popupPosition, editData, sendImage, enableVoiceInput, resetConversationLabel, windowStyle, headerStyle, defaultProcessingIcon, agentName, }) => {
    const store = useStore();
    const navigate = useNavigate();
    const onPortalRelativeNavigate = useCallback((relativePath) => {
        if (relativePath)
            navigate(relativePath);
    }, [navigate]);
    const [open, setOpen] = useState(false);
    const [iconAssetUrl, setIconUrl] = useState(null);
    useEffect(() => {
        if (iconAssetId) {
            const loadAssetUrl = async () => {
                try {
                    const iconAsset = await store.load('asset', iconAssetId);
                    const url = await getSignedAssetUrl(iconAsset, 'original', store);
                    if (url) {
                        setIconUrl(url);
                    }
                    else {
                        setIconUrl(null);
                    }
                }
                catch (error) {
                    console.error('Failed to get signed asset URL:', error);
                    setIconUrl(null);
                }
            };
            loadAssetUrl();
        }
        else {
            setIconUrl(null);
        }
    }, [iconAssetId, store]);
    const handleClose = () => {
        setOpen(false);
    };
    const descriptionElementRef = useRef(null);
    useEffect(() => {
        if (open) {
            descriptionElementRef.current?.focus();
        }
    }, [open]);
    const fabStyle = {
        position: 'fixed',
        bottom: editData ? '105px' : '20px',
        right: editData ? '105px' : '20px',
        zIndex: 1000,
    };
    const tokens = useMemo(() => {
        if (!store.currentPortal)
            return null;
        const tokensInstance = new Tokens(store.currentPortal);
        const locale = store.customerContext?.currentPage?.locale || store.customerContext?.locale;
        if (locale) {
            tokensInstance.setLocale(locale);
        }
        return tokensInstance;
    }, [
        store.currentPortal,
        store.customerContext?.currentPage?.locale,
        store.customerContext?.locale,
    ]);
    const messageConfig = useMemo(() => ({
        showItemReferences,
        questionStyle,
        responseStyle,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        hideRightRailOnMobile,
        enableVoiceInput,
        enableMultiRefinements,
        multiRefinementSubmitButtonText,
        maxItemsInRightRail,
        openSupportToolDelay: store.clientAIConfig?.openSupportToolDelay || 6000,
        openSupportToolMessage: tokens ? tokens.resolvePortalText(store.clientAIConfig?.openSupportToolMessage, 'openSupportToolMessage') : store.clientAIConfig?.openSupportToolMessage || '{{openSupportToolMessage}}',
        defaultProcessingIcon,
    }), [
        showItemReferences,
        questionStyle,
        responseStyle,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        hideRightRailOnMobile,
        enableVoiceInput,
        enableMultiRefinements,
        multiRefinementSubmitButtonText,
        maxItemsInRightRail,
        defaultProcessingIcon,
        store.clientAIConfig?.openSupportToolDelay,
        store.clientAIConfig?.openSupportToolMessage,
        tokens,
    ]);
    const mediaCardConfig = useMemo(() => ({
        courseCardShowPrice,
        courseCardShowTitle: courseCardShowTitle || true,
        courseCardShowInstructor: courseCardShowInstructor || false,
        courseCardShowImage: courseCardShowImage || false,
        courseCardShowDesc: (courseCardShowDescLines && courseCardShowDescLines > 0) || true,
        courseCardMaxLinesDescription: (courseCardShowDescLines || 2),
        courseCardShowInfoBar: false,
        courseCardShowActionButton: true,
    }), [
        courseCardShowImage,
        courseCardShowPrice,
        courseCardShowTitle,
        courseCardShowInstructor,
        courseCardShowDescLines,
    ]);
    return (_jsxs(ConversationStoreProvider, { value: new ConversationStoreAdapter(store), children: [_jsx(SelectionWizardProvider, { children: _jsx(ConversationDialog, { title: title, open: open, handleClose: handleClose, messageConfig: messageConfig, mediaCardConfig: mediaCardConfig, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, popupPosition: popupPosition, sendImage: sendImage, windowStyle: windowStyle, headerStyle: headerStyle, resetConversationLabel: resetConversationLabel, animatedProcessingImage: clientAIAnimatedProcessingIcon, enableVoiceInput: Boolean(enableVoiceInput), agentName: agentName }) }), _jsx(Fab, { color: "primary", "aria-label": "chat", onClick: () => setOpen(!open), sx: {
                    ...fabStyle,
                    backgroundColor,
                }, children: iconAssetUrl ? (_jsx("img", { src: iconAssetUrl, alt: "Chat Icon", style: { width: '100%', height: '100%', padding } })) : (_jsx(VirtualCoachIcon, { sx: { width: '100%', height: '100%', padding } })) })] }));
};
CoachFloatingWidget.defaultProps = {
    ...conversationDialogDefaults,
    iconAssetId: undefined,
    backgroundColor: undefined,
    padding: 1,
    editData: undefined,
    agentName: undefined,
};
CoachFloatingWidget.meta = {
    group: 'portal',
    name: 'Virtual Coach Floating Widget',
    sort: 40,
    description: 'Floating chat bubble to access AI chat.',
    props: [
        {
            key: 'iconAssetId',
            name: 'Chat Icon URL',
            type: 'asset',
            description: 'Custom icon image for the chat bubble.',
        },
        {
            key: 'backgroundColor',
            name: 'Background Floating Icon Color',
            type: 'color',
            description: 'Background color for the chat bubble.',
        },
        {
            key: 'padding',
            name: 'Icon Padding',
            type: 'number',
            description: 'Padding around the icon inside the chat bubble.',
        },
        {
            key: 'agentName',
            name: 'Agent Name',
            type: 'text',
            description: 'Optional agent identifier for AI reporting.',
        },
        ...conversationDialogMeta,
    ],
    defaultConfig: {
        type: 'component',
        component: 'VirtualCoachFloatingWidget',
        props: CoachFloatingWidget.defaultProps,
        // {
        //   title: 'Virtual Coach',
        //   conversational: true,
        //   showItemReferences: false,
        //   iconAssetId: undefined,
        //   padding: 1,
        //   courseCardShowImage: true,
        //   courseCardShowTitle: true,
        //   courseCardShowInstructor: false,
        //   courseCardShowPrice: false,
        //   courseCardShowDescLines: 2,
        // },
    },
};
export default CoachFloatingWidget;
//# sourceMappingURL=CoachFloatingWidget.js.map